import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Brand } from "@/components/AppShell";
import { Loading } from "@/components/common";
import { RolePicker, GradeSelect } from "@/pages/AuthPage";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Onboarding() {
  const { user, loading, setUser } = useAuth();
  const [role, setRole] = useState("student");
  const [grade, setGrade] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role) return <Navigate to="/dashboard" replace />;

  const save = async () => {
    if (role === "student" && !grade) return toast.error("Please choose a grade level");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/onboard", { role, grade_band: role === "student" ? grade : null });
      setUser(data);
      navigate(role === "student" ? "/dashboard" : "/billing", { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen hero-wash flex flex-col items-center justify-center px-4">
      <div className="mb-8"><Brand /></div>
      <div className="w-full max-w-md bg-white border border-border rounded-[18px] p-8 fade-up" data-testid="onboarding-card">
        <h1 className="text-2xl font-display font-semibold">Welcome, {user.name?.split(" ")[0]}!</h1>
        <p className="text-sm text-muted-foreground mt-1">Tell us how you'll use Scholar Campus.</p>
        <div className="mt-6 space-y-5">
          <div className="space-y-2"><Label>I am a...</Label><RolePicker role={role} setRole={setRole} /></div>
          {role === "student" && (
            <div className="space-y-2"><Label>Grade level</Label><GradeSelect value={grade} onChange={setGrade} testid="onboarding-grade-select" /></div>
          )}
          <Button className="w-full h-11" onClick={save} disabled={busy} data-testid="onboarding-submit-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Continue
          </Button>
        </div>
      </div>
    </div>
  );
}
